/**
 * FeedTabs — segmented control for filtering the home feed.
 *
 * A controlled component with four tabs: "For You", "Following",
 * "Trending", "Discover". Mirrors the SegmentGroup / Segment pattern
 * from dev/PersonalizationPanel.
 */
import { useState } from 'react';
import styled from 'styled-components';
import { media } from '@/theme/media';
import { Popover } from '@/ui';
import { Icon } from '@/icons';
import { FEED_TOPICS, type FeedTopicKey } from '@/core/data/feedTopics';
import { useSession } from '@/hooks';

/* ── Types ──────────────────────────────────────────────────────────────── */

export type FeedTabKey = 'for-you' | 'following' | 'trending' | 'discover';

export interface FeedTabsProps {
  active: FeedTabKey;
  onChange: (t: FeedTabKey) => void;
}

/* ── Styled primitives ───────────────────────────────────────────────────── */

// No overflow: hidden here — the For You tab hosts the topic-menu Popover, whose
// panel is position: absolute in-place (no portal, see ui/Popover) and would be
// clipped. Corner rounding is done per-edge child instead.
/* Tabs sit on a single hairline that runs the full column width, rather than
   inside a bordered segmented box. The box plus a fully-filled accent pill for
   the active tab put a second saturated accent block directly above the Post
   button, so two unrelated controls competed to be the loudest thing on screen.
   A filter is not the page's primary action and should not be painted like one. */
const SegmentGroup = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.glassBorder};
  width: 100%;

  ${media.mobile`
    flex-wrap: wrap;
  `}
`;

const Segment = styled.button<{ $active: boolean; $withChevron?: boolean }>`
  /* Extra right padding reserves room for the in-tab topic chevron so the
     label never sits under the icon (space is constant, no hover reflow). */
  position: relative;
  padding: ${({ $withChevron }) => ($withChevron ? '10px 30px 10px 14px' : '10px 14px')};
  @media (max-width: 767.98px) {
    padding: ${({ $withChevron }) => ($withChevron ? '10px 26px 10px 10px' : '10px')};
  }
  flex-shrink: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ $active, theme }) => ($active ? theme.color.textPrimary : theme.color.textMuted)};
  transition: color 0.12s;

  /* The active mark: a 2px accent bar sitting on the group's own hairline. */
  &::after {
    content: '';
    position: absolute;
    left: 14px;
    right: 14px;
    bottom: -1px;
    height: 2px;
    border-radius: 1px;
    background: ${({ $active, theme }) => ($active ? theme.color.accentCyan : 'transparent')};
  }

  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

/* ── Tab definitions ─────────────────────────────────────────────────────── */

const TABS: Array<{ key: FeedTabKey; label: string }> = [
  { key: 'for-you', label: 'For You' },
  { key: 'following', label: 'Following' },
  { key: 'trending', label: 'Trending' },
  { key: 'discover', label: 'Discover' },
];

/* ── Topic-menu styled pieces ─────────────────────────────────────────────── */

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Hosts the For You segment + its topic chevron as one visual tab. A wrapper is
// required because Segment is a <button> and the chevron is its own <button> —
// nesting them would be invalid HTML.
const ForYouTab = styled.div`
  position: relative;
  display: flex;
  flex-shrink: 0;
`;

// Anchor the Popover to the whole tab while keeping its chevron on the right.
// Only the chevron and open menu receive pointer events; the label underneath
// remains the tab-selection button. Avoid transforms that trap the menu's z-index.
const ChevronSlot = styled.div`
  position: absolute;
  left: 0;
  right: 4px;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  pointer-events: none;
  > div {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
  button,
  [role='menu'] {
    pointer-events: auto;
  }
`;

const ChevronBtn = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 2px;
  border-radius: ${({ theme }) => theme.radius.sm};

  /* Hover-revealed: invisible until the tab is hovered, the button is focused,
     or the menu is open. Touch devices have no hover, so it stays visible there
     rather than being an invisible tap target. */
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  transition: opacity 0.12s;

  ${ForYouTab}:hover &,
  &:focus-visible {
    opacity: 1;
  }
  @media (hover: none) {
    opacity: 1;
  }

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const TopicPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 240px;
`;

const TopicRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.color.glassBorder};
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -2px;
  }
`;

// Custom check indicator (never a raw <input type="checkbox"> nested in a menu —
// that mixes a form control into an ARIA menu, which is invalid; the row itself
// carries role="menuitemcheckbox" + aria-checked instead).
const CheckBox = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ $checked, theme }) => ($checked ? 'transparent' : theme.color.glassBorder)};
  background: ${({ $checked, theme }) => ($checked ? theme.color.accentCyan : 'transparent')};
  color: ${({ theme }) => theme.color.onAccent};
`;

const TopicNote = styled.p`
  margin: 4px 10px 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

/**
 * The For-You topic menu — a Popover attached to a chevron that only renders
 * while For You is the active tab. Opening it never switches tabs.
 */
function ForYouTopicMenu() {
  const { currentUser, updateProfile } = useSession();
  const [open, setOpen] = useState(false);
  // Optimistic local override of the server's disabled-topic list, so a toggle
  // reflects instantly and rolls back if the PATCH fails.
  const [override, setOverride] = useState<string[] | null>(null);
  const disabled = override ?? currentUser?.feedTopics ?? [];

  function toggle(key: FeedTopicKey) {
    const isOff = disabled.includes(key);
    const next = isOff ? disabled.filter((k) => k !== key) : [...disabled, key];
    const prev = disabled;
    setOverride(next);
    void updateProfile({ feedTopics: next }).then((ok) => {
      if (!ok) setOverride(prev);
    });
  }

  return (
    <Popover
      trigger={
        <ChevronBtn type="button" aria-label="For You topic filters" $open={open}>
          <Icon name="chevron-down" size={14} />
        </ChevronBtn>
      }
      open={open}
      onOpenChange={setOpen}
      align="start"
    >
      {/* No role="menu" here — the Popover's own panel already carries that role;
          nesting another one is a menu-in-menu ARIA bug. */}
      <TopicPanel aria-label="For You topics" data-testid="for-you-topics-panel">
        {FEED_TOPICS.map((t) => {
          const on = !disabled.includes(t.key);
          return (
            <TopicRow
              key={t.key}
              type="button"
              role="menuitemcheckbox"
              aria-checked={on}
              aria-label={`Show ${t.label} in For You`}
              onClick={() => toggle(t.key)}
            >
              {t.label}
              <CheckBox $checked={on} aria-hidden="true">
                {on && <Icon name="check" size={11} />}
              </CheckBox>
            </TopicRow>
          );
        })}
        <TopicNote>General always stays on.</TopicNote>
      </TopicPanel>
    </Popover>
  );
}

/* ── Component ───────────────────────────────────────────────────────────── */

export function FeedTabs({ active, onChange }: FeedTabsProps) {
  return (
    <Row>
      <SegmentGroup role="group" aria-label="Feed filter">
        {TABS.map(({ key, label }) => {
          // The For You tab hosts its topic chevron INSIDE the tab (owner spec:
          // hover-revealed, anchored to the tab — not parked at the row's end).
          // Only rendered while For You is active; opening never switches tabs.
          if (key === 'for-you') {
            const isActive = active === key;
            return (
              <ForYouTab key={key} data-testid="for-you-tab">
                <Segment
                  type="button"
                  $active={isActive}
                  $withChevron={isActive}
                  aria-pressed={isActive}
                  onClick={() => onChange(key)}
                >
                  {label}
                </Segment>
                {isActive && (
                  <ChevronSlot>
                    <ForYouTopicMenu />
                  </ChevronSlot>
                )}
              </ForYouTab>
            );
          }
          return (
            <Segment
              key={key}
              type="button"
              $active={active === key}
              aria-pressed={active === key}
              onClick={() => onChange(key)}
            >
              {label}
            </Segment>
          );
        })}
      </SegmentGroup>
    </Row>
  );
}
